<template>
  <div
    ref="footer"
    :class="[
      'dialog-footer flex-row justify-end gap-3',
      { 'dialog-footer--no-border': hideBorder },
    ]"
  >
    <slot />
  </div>
</template>

<script lang="ts" setup>
import {
  inject,
  onBeforeUnmount,
  onMounted,
  onUpdated,
  ref,
  useId,
  type Ref,
} from 'vue'
import { getIframeSession, isInsideIframe } from '@/auth/iframeSession'

const fullHeight = inject<Ref<boolean>>('dialogFullHeight', ref(false))
const footer = ref<HTMLElement>()
const id = useId()
let observer: ResizeObserver | undefined
let motionObserver: MutationObserver | undefined
let lastMessage = ''

const report = (): void => {
  const origin = getIframeSession()?.parentOrigin
  if (!origin || !isInsideIframe() || !fullHeight.value) return
  const buttons = Array.from(
    footer.value?.querySelectorAll('button, [role="button"]') ?? []
  )
    .map((button) => button.getBoundingClientRect())
    .filter((rect) => rect.width > 0 && rect.height > 0)
  const rect =
    buttons.length > 0
      ? {
          left: Math.min(...buttons.map((button) => button.left)),
          top: Math.min(...buttons.map((button) => button.top)),
          bottom: Math.max(...buttons.map((button) => button.bottom)),
        }
      : null
  const message = { type: 'HERMES_DIALOG_FOOTER', id, rect }
  const serialized = JSON.stringify(message)
  if (serialized === lastMessage) return
  lastMessage = serialized
  window.parent.postMessage(message, origin)
}

onMounted(() => {
  if (!isInsideIframe() || !fullHeight.value) return
  observer = new ResizeObserver(report)
  if (footer.value) observer.observe(footer.value)
  // Vue Motion changes the card's transform without resizing the footer.
  const card = footer.value?.closest('.own-dialog__card')
  if (card) {
    motionObserver = new MutationObserver(report)
    motionObserver.observe(card, {
      attributes: true,
      attributeFilter: ['style'],
    })
  }
  window.addEventListener('resize', report)
  report()
})
onUpdated(report)
onBeforeUnmount(() => {
  observer?.disconnect()
  motionObserver?.disconnect()
  window.removeEventListener('resize', report)
  const origin = getIframeSession()?.parentOrigin
  if (origin && lastMessage && isInsideIframe()) {
    window.parent.postMessage(
      { type: 'HERMES_DIALOG_FOOTER', id, rect: null },
      origin
    )
  }
})

withDefaults(defineProps<{ hideBorder?: boolean }>(), { hideBorder: false })
</script>

<style lang="scss" scoped>
.dialog-footer {
  padding: 24px;
  border-top: 0.5px solid var(--divider);

  &--no-border {
    border-top: none;
  }
}
</style>
